/**
 * OrientationMarkers - Anatomical Direction Indicators
 *
 * Displays A/P/L/R/H/F letters at viewport edges to indicate anatomical orientation.
 * This is SAFETY CRITICAL for proper radiological interpretation.
 *
 * Presentational only: the parent computes the labels from the geometry the
 * pane actually displays plus the viewport's LIVE flipHorizontal, via
 * `deriveOrientationLabels` in `@/viewport/radiologicalOrientation`, so the
 * letters reflect the REAL displayed orientation and can never silently assert
 * a fixed convention that disagrees with the pixels. Pass `null` to render
 * nothing (orientation unknown) — on a laterality overlay, no letters beats
 * letters that might be mirrored.
 */

import React from "react";
import type { OrientationLabels } from "@/viewport/radiologicalOrientation";

export interface OrientationMarkersProps {
  labels: OrientationLabels | null;
}

export const OrientationMarkers: React.FC<OrientationMarkersProps> = ({ labels }) => {
  // Don't render if orientation is unknown
  if (!labels) return null;

  // Common text style - large, bold, with shadow
  const markerStyle = "text-yellow-300 text-lg font-bold drop-shadow-[2px_2px_3px_rgba(0,0,0,0.9)]";

  return (
    <div className="absolute inset-0 pointer-events-none select-none">
      {/* Top marker. The data-testid on each edge is load-bearing for the unit
          test: which EDGE a letter sits on is the whole safety property, and a
          textContent assertion passes even with left/right transposed. */}
      {labels.top && (
        <div
          data-testid="orientation-marker-top"
          className={`absolute top-1 left-1/2 -translate-x-1/2 ${markerStyle}`}
        >
          {labels.top}
        </div>
      )}

      {/* Bottom marker */}
      {labels.bottom && (
        <div
          data-testid="orientation-marker-bottom"
          className={`absolute bottom-1 left-1/2 -translate-x-1/2 ${markerStyle}`}
        >
          {labels.bottom}
        </div>
      )}

      {/* Left marker */}
      {labels.left && (
        <div
          data-testid="orientation-marker-left"
          className={`absolute left-1 top-1/2 -translate-y-1/2 ${markerStyle}`}
        >
          {labels.left}
        </div>
      )}

      {/* Right marker */}
      {labels.right && (
        <div
          data-testid="orientation-marker-right"
          className={`absolute right-1 top-1/2 -translate-y-1/2 ${markerStyle}`}
        >
          {labels.right}
        </div>
      )}
    </div>
  );
};

export default OrientationMarkers;
